import { Input, Text, Textarea, tokens } from '@fluentui/react-components';
import { useLookupOptions } from '@moko/utils';
import { useOtherInfoContext } from '../OtherInfoContext';
import { isFieldReadOnly } from '../fieldReadOnly';
import { FieldWrapper } from './FieldWrapper';
import { useFieldReadOnly } from './fieldReadOnly';
import { SuggestionInput } from './SuggestionInput';
import type { FieldComponentProps } from './types';

export const StringField = ({
  field,
  fieldValue,
  isFieldDisabled,
  isSaved,
  validationMessage,
}: FieldComponentProps) => {
  const { handleOtherInfoChange, validateField, params } =
    useOtherInfoContext();
  const getLookupOptions = useLookupOptions(params.metadata);
  const readOnly = useFieldReadOnly(field);

  const updateValue = (value: string) => {
    handleOtherInfoChange(field.name, value);
    validateField(field.name, value);
  };

  if (field.rows && readOnly) {
    return (
      <FieldWrapper field={field} validationMessage={validationMessage}>
        <Text
          style={{
            display: 'block',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            minHeight: '32px',
            paddingInlineStart: tokens.spacingHorizontalM,
          }}
        >
          {fieldValue == null ? '' : String(fieldValue)}
        </Text>
      </FieldWrapper>
    );
  }

  if (field.rows) {
    return (
      <FieldWrapper field={field} validationMessage={validationMessage}>
        <Textarea
          id={field.name}
          resize="vertical"
          onChange={(_, newValue) => updateValue(newValue.value)}
          value={fieldValue == null ? '' : String(fieldValue)}
          readOnly={isSaved || isFieldReadOnly(field)}
          disabled={isFieldDisabled}
          rows={field.rows ?? 3}
          maxLength={field.maxLength}
        />
      </FieldWrapper>
    );
  }

  const text = fieldValue == null ? '' : String(fieldValue);
  const suggestions = getLookupOptions(field).map((option) => option.value);
  const canSuggest = suggestions.length > 0 && !isSaved && !readOnly;

  return (
    <FieldWrapper field={field} validationMessage={validationMessage}>
      {canSuggest ? (
        <SuggestionInput
          id={field.name}
          value={text}
          suggestions={suggestions}
          onChange={updateValue}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          disabled={isFieldDisabled}
        />
      ) : (
        <Input
          id={field.name}
          onChange={(_, newValue) => updateValue(newValue.value)}
          type="text"
          placeholder={field.placeholder}
          value={text}
          disabled={isFieldDisabled}
          readOnly={isSaved || isFieldReadOnly(field)}
          maxLength={field.maxLength}
        />
      )}
    </FieldWrapper>
  );
};
